import { useState } from 'react'
import { DialogTrigger } from 'react-aria-components'
import { Link, Outlet } from 'react-router-dom'
import type { GenericObject, ObjectProperties } from '../../shared/types'
import { useObjectActionMutationVariable, useObjectList } from '../api/object'
import { Button, ButtonLink } from '../components/button'
import { ClientFormatter } from '../components/client-formatter'
import { CurrencyFormatter } from '../components/currency-formatter'
import { DateFormatter } from '../components/date-formatter'
import { dateRangeToPrimitive, DateRange } from '../components/daterange-picker'
import { LocationFormatter } from '../components/location-formatter'
import { Paper } from '../components/paper'
import { ProposalVehicleFormatter } from '../components/proposal-vehicle-formatter'
import { Sidepanel } from '../components/sidepanel'
import { SourceFormatter } from '../components/source-formatter'
import { FilteringSidepanel } from '../objects/filtering-sidepanel'
import { Views } from '../objects/views'
import { List, useList } from '../scaffolding/list'
import { ListHeader } from '../scaffolding/list-header'
import { STATUS, STATUS_STYLES } from './const'

export type ParamsType = {
	date_from?: DateRange
	date_to?: DateRange
} & ObjectProperties

const filterToPrimitive = ({ date_from, date_to, ...rest }: ParamsType) => ({
	...rest,
	date_from: date_from ? dateRangeToPrimitive(date_from) : undefined,
	date_to: date_to ? dateRangeToPrimitive(date_to) : undefined,
})

export function Table() {
	const [params, setParams] = useList()
	const [filter, setFilter] = useState<ParamsType & { viewId?: string }>({})
	const { viewId, ...rest } = filter

	const query = useObjectList('proposal', {
		...filterToPrimitive(rest),
		...params,
	})

	const { mutate: refuse } = useObjectActionMutationVariable('proposal', 'refuse')
	const { mutate: refuseElsewhere } = useObjectActionMutationVariable(
		'proposal',
		'refuse-elsewhere',
	)

	const onSetView = (viewId?: string, filter?: ParamsType) => {
		setFilter({ ...filter, viewId } as ParamsType & { viewId?: string })
	}

	return (
		<Paper>
			<div className="flex justify-between gap-4 px-5 pt-4 sm:px-6 sm:pt-5 print:hidden">
				<h3 className="text-md font-bold">Seznam poptávek</h3>
				<div className="flex items-center gap-4">
					<ButtonLink variant="PRIMARY" to="./create">
						Vytvořit
					</ButtonLink>
					<DialogTrigger>
						<Button variant="SECONDARY">Filtrovat</Button>
						<Sidepanel heading="Filtrovat">
							{({ close }) => (
								<FilteringSidepanel<ParamsType>
									object="proposal"
									onClose={close}
									filter={filter}
									onSubmit={setFilter}
									viewId={viewId}
								/>
							)}
						</Sidepanel>
					</DialogTrigger>
				</div>
			</div>
			<Views onSetView={onSetView} viewId={viewId} filter={rest} object="proposal" />

			<List<GenericObject>
				id="proposal"
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						<ListHeader sortBy="id">ID</ListHeader>
						<th>Poptané vozidlo</th>
						<ListHeader sortBy="date_from">Termín</ListHeader>
						<th>Pobočka [in/out]</th>
						<th>Klient</th>
						<th>Zdroj</th>
						<th>Cena</th>
						<th>Dny</th>
						<th>Stav</th>
						<th className="print:hidden"></th>
						<th className="print:hidden"></th>
					</tr>
				}
			>
				{proposal => (
					<tr key={proposal.id}>
						<td>{proposal.id}</td>
						<td>
							<ProposalVehicleFormatter id={proposal.proposal_vehicle_id as string} />
							{proposal.poptane_vozidlo && (
								<div
									className="text-xs text-gray-600 mt-1 [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800"
									dangerouslySetInnerHTML={{
										__html: proposal.poptane_vozidlo as string,
									}}
								/>
							)}
						</td>
						<td>
							<DateFormatter
								date={proposal.date_from as string}
								format="d.M. HH:mm"
							/>
							<br />
							<DateFormatter date={proposal.date_to as string} format="d.M. HH:mm" />
						</td>
						<td>
							<LocationFormatter id={proposal.location_from as string} />
							<br />
							<LocationFormatter id={proposal.location_to as string} />
						</td>
						<td>
							{proposal.client_id ? (
								<Link to={`/svc/client/${proposal.client_id}`}>
									<ClientFormatter id={proposal.client_id as string} />
								</Link>
							) : (
								'–'
							)}
						</td>
						<td>
							{proposal.source ? (
								<SourceFormatter source={proposal.source as string} />
							) : (
								'–'
							)}
						</td>
						<td>
							<CurrencyFormatter value={proposal.price_min as number} />
							<br />
							<CurrencyFormatter value={proposal.price_max as number} />
						</td>
						{/* Změna pozice: Data pro Dny */}
						<td>{proposal.pocet_dni as string}</td>
						<td>
							<span
								className={`px-2 rounded-xl text-xxs font-semibold ${STATUS_STYLES[proposal.status as keyof typeof STATUS_STYLES]}`}
							>
								{STATUS[proposal.status as keyof typeof STATUS_STYLES]}
							</span>
						</td>
						<td className="print:hidden flex gap-2" width="10%">
							<ButtonLink variant="SECONDARY" size="SMALL" to={`${proposal.id}`}>
								Detail
							</ButtonLink>
							{proposal.status === 'new' ? (
								<>
									<Button
										variant="DANGER"
										size="SMALL"
										onPress={() => refuse({ id: proposal.id })}
									>
										Fuck&nbsp;off
									</Button>
									<Button
										variant="DANGER"
										size="SMALL"
										onPress={() => refuseElsewhere({ id: proposal.id })}
									>
										Řešeno jinde/Fuck off
									</Button>
								</>
							) : null}
						</td>
					</tr>
				)}
			</List>

			<Outlet />
		</Paper>
	)
}
